import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UiGridComponent } from '../../../../../shared/ui-grid/src/lib/ui-grid/ui-grid.component';
import { AgencyGridStore } from '../data/agency-grid.store';
import { AgencyGridRowViewModel, AgencyStore, LookupStore } from '@program-suite/data-common';
import { AuthStore } from '@program-suite/data-auth';
import { GridOptions } from 'libs/shared/ui-grid/src/lib/interfaces/grid-options';
import { GridEmitter } from 'libs/shared/ui-grid/src/lib/interfaces/grid-emitter';
import { Sort } from '@angular/material/sort';
import { ButtonComponent } from '@program-suite/ui';
import { ModalService, ModalType } from 'libs/shared/ui-common/src/lib/services/modal.service';
import { AddAgencyModalComponent } from '../add-agency-modal/add-agency-modal.component';
import { Router } from '@angular/router';

//TODO: Not loading properly
@Component({
  selector: 'lib-agencies-grid',
  imports: [CommonModule, UiGridComponent, ButtonComponent],
  templateUrl: './agency-grid.component.html',
  styleUrl: './agency-grid.component.scss',
})
export class AgencyGridComponent {
  agencyStore = inject(AgencyStore);
  agencyGridStore = inject(AgencyGridStore);
  lookupStore = inject(LookupStore);
  authStore = inject(AuthStore);
  defaultSortColumn = 'agencyName';
  defaultSortDirection = 'ASCENDING';
  totalNumberOfItems = computed<number>(() =>
    this.agencyGridStore.totalCount ? this.agencyGridStore.totalCount() ?? 0 : 0,
  );
  data = computed<AgencyGridRowViewModel[]>(() => (this.agencyGridStore.rows ? this.agencyGridStore.rows() ?? [] : []));
  gridOptions = signal<GridOptions>({
    filterData: [
      { name: 'Search', formControlName: 'searchTerm', type: 'search' },
      { name: 'State', formControlName: 'stateId', type: 'dropdown', lookup: this.lookupStore.states },
      { name: 'Program', formControlName: 'programId', type: 'dropdown', lookup: this.lookupStore.programs },
      { name: 'Tenant', formControlName: 'tenantId', type: 'dropdown', lookup: this.lookupStore.tenants, hideFilter: !this.authStore.isAdmin() },
      {
        name: 'Status',
        formControlName: 'status',
        type: 'dropdown',
        lookup: signal([
          { id: 'active', name: 'Active' },
          { id: 'inactive', name: 'Inactive' },
        ]),
      },
    ],
    filterValues: this.agencyGridStore.filterValues,
    openFilters: true,
    columns: [
      { header: 'Agency Name', controlName: 'agencyName' },
      { header: 'Agency Address', controlName: 'fullAddress' },
      { header: 'Programs', controlName: 'program' },
      { header: 'Tenant', controlName: 'tenantName', hideColumn: !this.authStore.isAdmin() },
      { header: 'Status', controlName: 'status' },
      { header: 'Created', controlName: 'createDate', displayPipe: 'date' },
      { header: 'Number of Users', controlName: 'agentCount' },
      {
        header: 'Manage',
        controlName: 'action',
        actionColumnitems: [
          {
            type: 'icon',
            buttonAction: 'action',
            conditionalDisplay: { calculateDisplay: () => 'eye', column: 'status' },
          },
        ],
      },
    ],
    data: this.data,
    totalNumberOfItems: this.totalNumberOfItems,
    frontEndPagination: false,
    fetchData: false,
    displaySearch: false,
  });

  constructor(private modalService: ModalService, private router: Router) {}

  ngOnInit(): void {
    if (this.authStore.isWholesale()) {
      this.lookupStore.loadLookup('agencies');
    }
    this.lookupStore.loadLookup('states');
    this.lookupStore.loadLookup('programs');
    if (this.authStore.isAdmin()) {
      this.lookupStore.loadLookup('tenants');
    }
  }

  fetchAll(rawParams?: any) {
    const { searchTerm, sortOrder, pageSize, pageNumber, programId, agencyId, stateId, status, tenantId } = rawParams;
    if (agencyId) {
      this.lookupStore.loadLookup('agents', agencyId);
    }
    const newSortOrder = this.sortField(sortOrder);
    const params = {
      PageSize: pageSize ? pageSize : 20,
      PageNumber: pageNumber ? pageNumber : 1,
      SearchTerm: searchTerm?.length > 0 ? searchTerm : '',
      ProgramId: programId ? programId : '',
      TenantId: tenantId ? tenantId : '',
      AgencyId: agencyId ? agencyId : '',
      StateId: stateId ? stateId : '',
      Status: status ? status : '',
      SortField: newSortOrder
        ? newSortOrder.active && newSortOrder.direction
          ? newSortOrder.active
          : this.defaultSortColumn
        : this.defaultSortColumn,
      SortDirection: newSortOrder ? newSortOrder.direction ?? this.defaultSortDirection : this.defaultSortDirection,
    };
    this.agencyGridStore.getAgencyGrid(params);
  }

  addAgency() {
    this.modalService.open(AddAgencyModalComponent, {
      modalType: ModalType.FullScreen,
      data: {
        title: 'Add Agency',
        formType: 'add',
      },
    });
  }

  sortField(sort: Sort) {
    let activeSort: string = this.defaultSortColumn;
    let direction: string | null = this.defaultSortDirection;
    if (sort !== null && sort !== undefined) {
      activeSort = sort.active;
      switch (true) {
        case sort.direction === 'desc':
          direction = 'DESCENDING';
          break;
        case sort.direction === 'asc':
          direction = 'ASCENDING';
          break;
        case sort.direction === '':
          direction = null;
          break;
      }
    }
    return { active: activeSort, direction: direction };
  }

  actionEvent(event: GridEmitter) {
    if (event.action === 'fetchAll') {
      this.fetchAll(event.data);
    } else if (event.action === 'formChanges') {
      this.fetchAll(event.data);
    } else if (event.action === 'clearFilters') {
      this.fetchAll(event.data);
    } else if (event.action === 'onDestroy') {
      this.agencyGridStore.setFilterValues(event.data ?? {});
    } else if (event.action === 'action') {
      this.action(event.data);
    }
  }

  action(data: any) {
    this.router.navigate(['/agency'], { queryParams: { agencyId: data.agencyId } });
  }
}
